import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trash, ShoppingCart, MessageCircle, FileDown } from 'lucide-react';
import jsPDF from 'jspdf';
import { useCart } from '../contexts/CartContext';
import { WHATSAPP_NUMBER, BUSINESS_NAME } from '../config';

const fmt = (n) => Number(n || 0).toLocaleString('es-AR');
const unit = (item) => Number(item.price ?? item.unitPrice ?? 0);
const line = (item) => unit(item) * Number(item.quantity || 0);
const itemLabel = (item) => `${item.name}${item.variant ? ` (${item.variant}cm)` : ''}`;

function ItemNotes({ id, initialNote = '', onChange }) {
  const [value, setValue] = useState(initialNote);
  const timer = useRef(null);
  const max = 140;

  useEffect(() => { setValue(initialNote || ''); }, [initialNote]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onChange(value), 400);
    return () => clearTimeout(timer.current);
  }, [value, onChange]);

  const remaining = max - value.length;

  return (
    <div className="mt-3">
      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Observaciones
      </label>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value.slice(0, max))}
        placeholder="Ej: 18 cm de dulce de leche. 24 cm de membrillo. Sin nuez."
        className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-pink-200 focus:border-pink-300 p-3 text-sm outline-none"
        rows={2}
      />
      <div className="mt-1 flex items-center">
        <span className="ml-auto text-xs text-gray-400">{remaining}</span>
      </div>
    </div>
  );
}

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, updateItemNote } = useCart();
  const total = cart.reduce((sum, item) => sum + line(item), 0);

  const buildWhatsappMessage = () => {
    const lineas = cart.map((item) => {
      const base = `- ${itemLabel(item)} x${item.quantity} - $${fmt(line(item))}`;
      return item.note ? `${base}\n  Obs: ${item.note}` : base;
    });
    return (
      `Hola! Quiero *confirmar fecha* para el siguiente pedido de ${BUSINESS_NAME}:\n\n` +
      lineas.join('\n') +
      `\n\nTotal: $${fmt(total)}\n\n` +
      `Quedo atento/a a la disponibilidad para la fecha que necesito. ¡Gracias!`
    );
  };

  const handleWhatsapp = () => {
    const texto = encodeURIComponent(buildWhatsappMessage());
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${texto}`, '_blank');
  };

  const handleDownloadPdf = () => {
    const doc = new jsPDF();
    let y = 20;

    doc.setFontSize(18);
    doc.text(BUSINESS_NAME, 14, y);
    y += 8;
    doc.setFontSize(11);
    doc.setTextColor(120);
    doc.text(`Pedido generado el ${new Date().toLocaleDateString('es-AR')}`, 14, y);
    doc.setTextColor(0);
    y += 12;

    doc.setFontSize(12);
    doc.text('Producto', 14, y);
    doc.text('Cant.', 118, y);
    doc.text('Precio', 140, y);
    doc.text('Subtotal', 170, y);
    y += 2;
    doc.line(14, y, 196, y);
    y += 8;

    cart.forEach((item) => {
      doc.text(itemLabel(item), 14, y);
      doc.text(String(item.quantity), 118, y);
      doc.text(`$${fmt(unit(item))}`, 140, y);
      doc.text(`$${fmt(line(item))}`, 170, y);
      y += 6;
      if (item.note) {
        doc.setFontSize(9);
        doc.setTextColor(130);
        const notaLines = doc.splitTextToSize(`Obs: ${item.note}`, 175);
        doc.text(notaLines, 18, y);
        y += 5 * notaLines.length;
        doc.setFontSize(12);
        doc.setTextColor(0);
      }
      y += 3;
    });

    y += 2;
    doc.line(14, y, 196, y);
    y += 10;
    doc.setFontSize(14);
    doc.text(`Total: $${fmt(total)}`, 14, y);

    y += 14;
    doc.setFontSize(10);
    doc.setTextColor(150);
    const nota = doc.splitTextToSize(
      'Este documento es un presupuesto orientativo. Los pedidos se preparan con anticipación: ' +
        'la fecha de entrega queda confirmada recién al coordinar por WhatsApp.',
      182
    );
    doc.text(nota, 14, y);

    doc.save(`pedido-${BUSINESS_NAME.replace(/\s+/g, '-').toLowerCase()}.pdf`);
  };

  if (cart.length === 0) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
        <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Tu carrito está vacío</h2>
        <p className="text-gray-600">¡Agrega algunas delicias para empezar!</p>
        <Link to="/productos" className="mt-4 inline-block bg-pink-500 text-white px-6 py-3 rounded-full font-semibold">
          Ver Productos
        </Link>
      </motion.div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-8 flex items-center">
        <ShoppingCart className="w-8 h-8 mr-2" /> Tu Carrito
      </h1>

      <div className="space-y-6">
        {cart.map((item) => {
          const u = unit(item);
          const sub = line(item);

          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-white rounded-2xl shadow-md"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-lg" />
                  <div>
                    <h3 className="font-bold text-gray-800">{itemLabel(item)}</h3>
                    {item.detail && <p className="text-gray-600 text-sm">{item.detail}</p>}
                    <p className="text-gray-600">
                      {u > 0 ? <>${fmt(u)} c/u</> : <>Consultar</>}
                    </p>
                    <div className="flex items-center mt-2">
                      <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="px-2 py-1 bg-gray-200 rounded">-</button>
                      <span className="mx-2">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="px-2 py-1 bg-gray-200 rounded">+</button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="font-bold text-pink-500">${fmt(sub)}</span>
                  <button onClick={() => removeFromCart(item.id)} className="text-red-500 hover:text-red-700">
                    <Trash className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <ItemNotes
                id={item.id}
                initialNote={item.note}
                onChange={(note) => updateItemNote(item.id, note)}
              />
            </motion.div>
          );
        })}
      </div>

      <div className="mt-8 p-6 bg-white rounded-2xl shadow-lg space-y-4">
        <div className="text-xl font-bold text-right">Total: ${fmt(total)}</div>
        <p className="text-sm text-gray-500">
          Este pedido todavía no está confirmado. Al escribirnos por WhatsApp coordinamos si la fecha
          que necesitás es posible.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <button
            onClick={handleDownloadPdf}
            className="flex items-center justify-center gap-2 border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-full font-semibold hover:bg-gray-50 transition-all"
          >
            <FileDown className="w-5 h-5" />
            <span>Descargar PDF</span>
          </button>
          <button
            onClick={handleWhatsapp}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition-all"
          >
            <MessageCircle className="w-5 h-5" />
            <span>Confirmar fecha por WhatsApp</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Cart;
